import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, ApiError } from '../../api';
import { t, LANGUAGES } from '../../i18n';
import { showToast } from '../../components/ui/toast';

type Step = 'account' | 'school' | 'email' | 'finish';

interface EmailProviderOption { id: string; label: string; key_prefix: string; hint: string }

/** First-run setup wizard: create the first admin + school, then optionally configure
 *  school details and email delivery, then finish. Every step after the account is
 *  skippable — the operator can reach the dashboard immediately and configure the rest
 *  from Settings. Reuses the normal Settings endpoints once the admin session exists. */
@customElement('cadence-setup-wizard')
export class CadenceSetupWizard extends LitElement {
  @state() private step: Step = 'account';
  @state() private submitting = false;

  // Step 1 — account + school name.
  @state() private schoolName = '';
  @state() private firstName = '';
  @state() private lastName = '';
  @state() private email = '';
  @state() private password = '';
  @state() private confirm = '';
  @state() private accountError = '';

  // Step 2 — school details. EUR default — Europe is the primary geography.
  @state() private currency = 'EUR';
  @state() private timezone = 'UTC';
  @state() private language = 'en';
  @state() private contactEmail = '';

  // Step 3 — email.
  @state() private emailLoaded = false;
  @state() private hasMasterKey = true;
  @state() private providers: EmailProviderOption[] = [];
  @state() private emailProvider = 'sendgrid';
  @state() private apiKey = '';
  @state() private fromAddress = '';
  @state() private fromName = '';

  static styles = css`
    :host { display: grid; place-items: center; min-height: 100vh; padding: var(--space-4); }
    .card {
      width: 100%; max-width: 640px; background: var(--color-bg-primary);
      border: 1px solid var(--color-border); border-radius: var(--radius-lg);
      box-shadow: var(--shadow-md); padding: var(--space-6); box-sizing: border-box;
    }
    .brand { display: flex; align-items: center; gap: 10px; margin-bottom: var(--space-4); }
    .brand .dot { width: 14px; height: 14px; border-radius: 50%; background: var(--color-primary); }
    .brand h1 { margin: 0; font-size: 1.3rem; }
    .steps { display: flex; gap: 8px; margin-bottom: var(--space-5); }
    .steps .s { flex: 1; height: 6px; border-radius: 999px; background: var(--color-border); }
    .steps .s.active, .steps .s.done { background: var(--color-primary); }
    h2 { margin: 0 0 4px; font-size: 1.15rem; }
    p.sub { margin: 0 0 var(--space-4); color: var(--color-text-secondary); font-size: 0.92rem; }
    form { display: flex; flex-direction: column; gap: var(--space-4); }
    .grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: var(--space-4); }
    .actions { display: flex; justify-content: space-between; align-items: center; gap: 10px; margin-top: var(--space-2); }
    .actions .spacer { flex: 1; }
    .hint { color: var(--color-text-secondary); font-size: 0.82rem; }
    .warn {
      border: 1px solid var(--color-warning, #b8860b); background: color-mix(in srgb, var(--color-warning, #b8860b) 12%, transparent);
      color: var(--color-text-primary); border-radius: var(--radius-md); padding: var(--space-3); font-size: 0.86rem;
    }
    .next-grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-3); margin-bottom: var(--space-4); }
    .next {
      text-align: left; border: 1px solid var(--color-border); border-radius: var(--radius-md);
      padding: var(--space-3); background: var(--color-bg-secondary); cursor: pointer; font: inherit; color: inherit;
    }
    .next:hover { border-color: var(--color-primary); }
    .next strong { display: block; margin-bottom: 2px; }
    .next span { color: var(--color-text-secondary); font-size: 0.82rem; }
    @media (max-width: 560px) { .grid, .next-grid { grid-template-columns: 1fr; } }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    // Sensible time-zone default from the browser.
    try { this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { /* keep UTC */ }
    this.resume();
  }

  /** If an admin already exists (e.g. the page was reloaded mid-wizard), skip step 1. */
  private async resume() {
    try {
      const s = await api.get<{ initialized: boolean; needs_admin: boolean }>('/setup/status');
      if (!s.needs_admin && this.step === 'account') this.step = 'school';
    } catch { /* stay on account */ }
  }

  private timezones(): string[] {
    try { return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? ['UTC']; }
    catch { return ['UTC']; }
  }

  // ---- Step 1: account ----
  private async submitAccount() {
    this.accountError = '';
    if (!this.schoolName.trim()) { this.accountError = t('setup.account.schoolName'); return; }
    if (this.password.length < 8) { this.accountError = t('setup.account.weak'); return; }
    if (this.password !== this.confirm) { this.accountError = t('setup.account.mismatch'); return; }
    this.submitting = true;
    try {
      await api.post('/setup/admin', {
        school_name: this.schoolName.trim(),
        first_name: this.firstName.trim(),
        last_name: this.lastName.trim(),
        email: this.email.trim(),
        password: this.password,
      });
      // Admin session cookie is now set; prefill the contact email and continue.
      this.contactEmail = this.email.trim();
      this.step = 'school';
    } catch (e) {
      this.accountError = e instanceof ApiError ? e.message : (e as Error).message;
    } finally { this.submitting = false; }
  }

  // ---- Step 2: school ----
  private async submitSchool() {
    this.submitting = true;
    try {
      await api.put('/settings/school', {
        timezone: this.timezone,
        default_language: this.language,
        contact_email: this.contactEmail.trim() || undefined,
      });
      if (this.currency) await api.put('/settings/currency', { primary_currency: this.currency });
      this.gotoEmail();
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.submitting = false; }
  }

  private async gotoEmail() {
    this.step = 'email';
    if (this.emailLoaded) return;
    try {
      const cfg = await api.get<{ has_master_key: boolean; provider: string; from_address: string; from_name: string; providers: EmailProviderOption[] }>('/settings/email');
      this.providers = cfg.providers ?? [];
      this.hasMasterKey = cfg.has_master_key;
      this.emailProvider = cfg.provider || this.providers[0]?.id || 'sendgrid';
      this.fromAddress = cfg.from_address || this.contactEmail;
      this.fromName = cfg.from_name || this.schoolName;
    } catch { /* leave defaults */ }
    finally { this.emailLoaded = true; }
  }

  // ---- Step 3: email ----
  private async submitEmail() {
    if (!this.apiKey.trim()) { this.step = 'finish'; return; } // nothing entered → treat as skip
    this.submitting = true;
    try {
      await api.put('/settings/email', {
        enabled: true,
        provider: this.emailProvider,
        api_key: this.apiKey.trim(),
        from_address: this.fromAddress.trim(),
        from_name: this.fromName.trim(),
      });
      this.step = 'finish';
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.submitting = false; }
  }

  // ---- Step 4: finish ----
  private async complete(target = '/admin/dashboard') {
    this.submitting = true;
    try {
      await api.post('/setup/complete');
      // Full reload so boot() re-probes the session + setup status and routes normally.
      window.location.href = target;
    } catch (e) {
      showToast((e as Error).message, 'error');
      this.submitting = false;
    }
  }

  private renderSteps() {
    const order: Step[] = ['account', 'school', 'email', 'finish'];
    const idx = order.indexOf(this.step);
    return html`<div class="steps">
      ${order.map((_, i) => html`<span class="s ${i < idx ? 'done' : ''} ${i === idx ? 'active' : ''}"></span>`)}
    </div>`;
  }

  render() {
    return html`
      <div class="card">
        <div class="brand"><span class="dot"></span><h1>${t('setup.brand')}</h1></div>
        ${this.renderSteps()}
        ${this.step === 'account' ? this.renderAccount() : ''}
        ${this.step === 'school' ? this.renderSchool() : ''}
        ${this.step === 'email' ? this.renderEmail() : ''}
        ${this.step === 'finish' ? this.renderFinish() : ''}
      </div>
    `;
  }

  private renderAccount() {
    return html`
      <h2>${t('setup.account.title')}</h2>
      <p class="sub">${t('setup.account.subtitle')}</p>
      <form @submit=${(e: Event) => { e.preventDefault(); this.submitAccount(); }}>
        ${this.accountError ? html`<p class="hint" style="color:var(--color-danger)" role="alert">${this.accountError}</p>` : ''}
        <cadence-input label=${t('setup.account.schoolName')} placeholder=${t('setup.account.schoolNamePlaceholder')}
          .value=${this.schoolName} @input=${(e: CustomEvent) => (this.schoolName = e.detail)}></cadence-input>
        <div class="grid">
          <cadence-input label=${t('setup.account.firstName')} .value=${this.firstName}
            @input=${(e: CustomEvent) => (this.firstName = e.detail)}></cadence-input>
          <cadence-input label=${t('setup.account.lastName')} .value=${this.lastName}
            @input=${(e: CustomEvent) => (this.lastName = e.detail)}></cadence-input>
        </div>
        <cadence-input label=${t('setup.account.email')} type="email" autocomplete="username"
          .value=${this.email} @input=${(e: CustomEvent) => (this.email = e.detail)}></cadence-input>
        <div class="grid">
          <cadence-input label=${t('setup.account.password')} type="password" autocomplete="new-password"
            .value=${this.password} @input=${(e: CustomEvent) => (this.password = e.detail)}></cadence-input>
          <cadence-input label=${t('setup.account.confirmPassword')} type="password" autocomplete="new-password"
            .value=${this.confirm} @input=${(e: CustomEvent) => (this.confirm = e.detail)}></cadence-input>
        </div>
        <div class="actions">
          <span class="spacer"></span>
          <cadence-button type="submit" ?loading=${this.submitting} @click=${() => this.submitAccount()}>
            ${t('setup.account.submit')}
          </cadence-button>
        </div>
      </form>
    `;
  }

  private renderSchool() {
    return html`
      <h2>${t('setup.school.title')}</h2>
      <p class="sub">${t('setup.school.subtitle')}</p>
      <form @submit=${(e: Event) => { e.preventDefault(); this.submitSchool(); }}>
        <div class="grid">
          <cadence-currency-select label=${t('setup.school.currency')} allowAdd .value=${this.currency}
            @change=${(e: CustomEvent) => (this.currency = e.detail)}></cadence-currency-select>
          <cadence-select label=${t('setup.school.language')} .value=${this.language}
            .options=${LANGUAGES.map((l) => ({ value: l.code, label: l.label }))}
            @change=${(e: CustomEvent) => (this.language = e.detail)}></cadence-select>
        </div>
        <cadence-select label=${t('setup.school.timezone')} .value=${this.timezone}
          .options=${this.timezones().map((tz) => ({ value: tz, label: tz }))}
          @change=${(e: CustomEvent) => (this.timezone = e.detail)}></cadence-select>
        <cadence-input label=${t('setup.school.contactEmail')} type="email" .value=${this.contactEmail}
          @input=${(e: CustomEvent) => (this.contactEmail = e.detail)}></cadence-input>
        <p class="hint">${t('setup.school.currencyHint')}</p>
        <div class="actions">
          <cadence-button variant="ghost" @click=${() => this.gotoEmail()}>${t('setup.skip')}</cadence-button>
          <span class="spacer"></span>
          <cadence-button type="submit" ?loading=${this.submitting} @click=${() => this.submitSchool()}>
            ${t('setup.school.save')}
          </cadence-button>
        </div>
      </form>
    `;
  }

  private renderEmail() {
    const meta = this.providers.find((p) => p.id === this.emailProvider);
    return html`
      <h2>${t('setup.email.title')}</h2>
      <p class="sub">${t('setup.email.subtitle')}</p>
      <form @submit=${(e: Event) => { e.preventDefault(); this.submitEmail(); }}>
        ${!this.hasMasterKey ? html`<div class="warn">${t('setup.email.noMasterKey')}</div>` : ''}
        <cadence-select label=${t('setup.email.provider')} .value=${this.emailProvider}
          .options=${this.providers.map((p) => ({ value: p.id, label: p.label }))}
          @change=${(e: CustomEvent) => (this.emailProvider = e.detail)}></cadence-select>
        <cadence-input label=${t('setup.email.apiKey')} type="password" .value=${this.apiKey}
          ?disabled=${!this.hasMasterKey} @input=${(e: CustomEvent) => (this.apiKey = e.detail)}></cadence-input>
        ${meta ? html`<p class="hint">${meta.hint}</p>` : ''}
        <div class="grid">
          <cadence-input label=${t('setup.email.fromAddress')} type="email" .value=${this.fromAddress}
            @input=${(e: CustomEvent) => (this.fromAddress = e.detail)}></cadence-input>
          <cadence-input label=${t('setup.email.fromName')} .value=${this.fromName}
            @input=${(e: CustomEvent) => (this.fromName = e.detail)}></cadence-input>
        </div>
        <div class="actions">
          <cadence-button variant="ghost" @click=${() => (this.step = 'finish')}>${t('setup.skip')}</cadence-button>
          <span class="spacer"></span>
          <cadence-button type="submit" ?loading=${this.submitting} ?disabled=${!this.hasMasterKey}
            @click=${() => this.submitEmail()}>${t('setup.email.save')}</cadence-button>
        </div>
      </form>
    `;
  }

  private renderFinish() {
    const next = (path: string, title: string, body: string) => html`
      <button class="next" @click=${() => this.complete(path)}>
        <strong>${title}</strong><span>${body}</span>
      </button>`;
    return html`
      <h2>${t('setup.finish.title')}</h2>
      <p class="sub">${t('setup.finish.subtitle')}</p>
      <div class="next-grid">
        ${next('/admin/teachers', t('setup.finish.addTeachers'), t('setup.finish.addTeachersBody'))}
        ${next('/admin/students', t('setup.finish.addStudents'), t('setup.finish.addStudentsBody'))}
        ${next('/admin/settings/billing', t('setup.finish.configureFees'), t('setup.finish.configureFeesBody'))}
        ${next('/admin/settings/features', t('setup.finish.features'), t('setup.finish.featuresBody'))}
      </div>
      <div class="actions">
        <span class="spacer"></span>
        <cadence-button ?loading=${this.submitting} @click=${() => this.complete()}>${t('setup.finish.go')}</cadence-button>
      </div>
    `;
  }
}
